import {WebCallService} from "@/utilities/services/WebCallService";
import {PathHelper} from "@/utilities/helpers/PathHelper";
import {ErrorLocationType, HttpType, TicketType} from "@/utilities/enums/Enums";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import {GuestRequest, OldUploadRequest, StaticTicketRequest} from "@/models/api/old/requests";
import ProjectFile from "@/models/entities/old/ProjectFile";
import NewProjectFile from "@/models/entities/ProjectFile";
import {DataResponse, GeneralOldResponse} from "@/models/api/old/responses";
import {Block} from "@/models/helpers/Block";
import {useGlobalStore} from "@/stores/GlobalStore";
import GeneralResponse from "@/models/api/GeneralResponse";
import {TicketFileInfoResponse} from "@/models/entities/ModelHelpers";
import {
    convertNewProjectFileToOld,
} from "@/utilities/helpers/Converters";

export default class GuestService {
    private webCaller: WebCallService;
    private uriHelper: PathHelper;

    constructor() {
        this.webCaller = new WebCallService(false);
        this.uriHelper = new PathHelper();
    }


    /* --- AUTH --- */


    public async getToken(guid:string, mail:string, errorLocationType:ErrorLocationType):Promise<string|null>{
        const uri = this.uriHelper.guest.getCheckAuthMailPath();
        const requestBody = new GuestRequest(guid, mail);

        const resp = await this.webCaller.httpSend<string|GeneralOldResponse>(HttpType.post, uri, requestBody);
        if (!resp || (typeof resp !== "string" && !(resp as GeneralOldResponse).success)) {
            await ErrorHandler.handleOldError(resp as GeneralOldResponse, errorLocationType);
            return null;
        }

        return resp as string;
    }

    public async checkOtp(guid:string, mail:string, otp:string, errorLocationType:ErrorLocationType):Promise<string|null>{
        const uri = this.uriHelper.guest.getCheckOtpPath();
        const requestBody = new GuestRequest(guid, mail, otp);

        const resp = await this.webCaller.httpSend<string|GeneralOldResponse>(HttpType.post, uri, requestBody);
        if (!resp || (typeof resp !== "string" && !(resp as GeneralOldResponse).success)) {
            await ErrorHandler.handleOldError(resp as GeneralOldResponse, errorLocationType);
            return null;
        }

        return resp as string;
    }

    public async checkPwd(guid:string, pwd:string, errorLocationType:ErrorLocationType):Promise<string|null>{
        const uri = this.uriHelper.guest.getCheckPwdPath();
        const requestBody = new StaticTicketRequest(guid, pwd);

        const resp = await this.webCaller.httpSend<string|GeneralOldResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || (typeof resp !== "string" && !(resp as GeneralOldResponse).success)) {
            await ErrorHandler.handleOldError(resp as GeneralOldResponse, errorLocationType);
            return null;
        }

        return resp as string;
    }


    /* --- FILE --- */



    public async getTicketFileInfo(guid:string, errorLocationType:ErrorLocationType, isStatic:boolean=false):Promise<TicketFileInfoResponse|null>{
        const uri = isStatic
            ? this.uriHelper.guest.getStaticTicketFileInfoPath(guid)
            : this.uriHelper.guest.getTicketFileInfoPath(guid);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.get, uri);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as TicketFileInfoResponse;
    }

    public async downloadFile(guid:string, callback:Function|null, errorLocationType:ErrorLocationType, isStatic:boolean=false):Promise<boolean|null>{
        const globalStore = useGlobalStore();
        const uri = isStatic
            ? this.uriHelper.guest.getStaticDownloadFilePath(guid)
            : this.uriHelper.guest.getDownloadFilePath(guid);

        globalStore.showProgress();

        const resp = await this.webCaller.downloadFile(uri, callback);
        if (!resp || !resp.success) {
            globalStore.success.show = false;
            await ErrorHandler.handleOldError(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async initializeFile (file:ProjectFile, ticketGuid:string|null, errorLocationType:ErrorLocationType):Promise<ProjectFile|null>{
        const uri = this.uriHelper.guest.getInitializeFilePath();
        const requestBody = new OldUploadRequest(file, ticketGuid);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (!resp || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }


        return convertNewProjectFileToOld(resp.data as NewProjectFile);
    }

    public async abortUpload(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.guest.getAbortUploadPath(uploadGuid);

        const resp = await this.webCaller.httpSend<GeneralOldResponse>(HttpType.post, uri);
        if (!resp || !resp.success) {
            await ErrorHandler.handleOldError(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async uploadBlock(uploadGuid:string, block:Block, callback:Function|null = null, abortSignal:AbortSignal, errorLocationType:ErrorLocationType):Promise<string|null>{
        const uri = this.uriHelper.guest.getUploadBlockPath(uploadGuid, block.index);

        const resp = await this.webCaller.uploadBlock(uploadGuid, block, uri, errorLocationType, callback, abortSignal);
        if (!resp || !(resp as GeneralOldResponse).success) {
            await ErrorHandler.handleOldError(resp as GeneralOldResponse, errorLocationType);
            return null;
        }

        return resp as string;
    }

    public async getMissingBlocks(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<Array<number>|null>{
        const uri = this.uriHelper.guest.getMissingBlocksPath(uploadGuid);

        const resp = await this.webCaller.httpSend<DataResponse>(HttpType.get, uri);
        if (!resp || !resp.success) {
            await ErrorHandler.handleOldError(resp, errorLocationType);
            return null;
        }

        return resp.data as Array<number>;
    }

    public async completeUpload(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.guest.getCompleteUploadPath(uploadGuid);

        const resp = await this.webCaller.httpSend<GeneralOldResponse>(HttpType.post, uri);
        if (!resp || !resp.success) {
            await ErrorHandler.handleOldError(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }


    /* --- TICKET --- */


    public async getTicketType(guid:string, errorLocationType:ErrorLocationType):Promise<TicketType|null>{
        const uri = this.uriHelper.guest.getTicketTypePath(guid);

        const resp = await this.webCaller.httpSend<number|GeneralOldResponse>(HttpType.get, uri);
        if (resp === null || resp === undefined || (typeof resp !== "number" && !(resp as GeneralOldResponse).success)) {
            await ErrorHandler.handleOldError(resp as GeneralOldResponse, errorLocationType);
            return null;
        }

        return Number(resp) as TicketType;
    }
}